import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/AuthContext'
import type { AppUser, UserStatus } from '@/lib/auth'

export default function AdminUsersPage() {
  const { appUser } = useAuth()
  const [users, setUsers] = useState<AppUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const isAdmin = appUser?.role === 'admin' && appUser?.status === 'approved'

  const loadUsers = async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('app_users')
      .select('*')
      .order('first_seen', { ascending: false })
    if (error) setError(error.message)
    else setUsers((data as AppUser[]) || [])
    setLoading(false)
  }

  useEffect(() => {
    if (isAdmin) loadUsers()
    else setLoading(false)
  }, [isAdmin])

  const setStatus = async (id: string, status: UserStatus) => {
    const { error } = await supabase.from('app_users').update({ status }).eq('id', id)
    if (error) { setError(error.message); return }
    setUsers(prev => prev.map(u => (u.id === id ? { ...u, status } : u)))
  }

  const removeUser = async (id: string, email: string) => {
    if (!confirm(`Remove ${email}? They will need to log in again to reappear as pending.`)) return
    const { error } = await supabase.from('app_users').delete().eq('id', id)
    if (error) { setError(error.message); return }
    setUsers(prev => prev.filter(u => u.id !== id))
  }

  if (!isAdmin) {
    return (
      <div className="p-6">
        <p className="text-[var(--color-text-muted)]">You do not have permission to view this page.</p>
      </div>
    )
  }

  const statusBadge = (status: UserStatus) => {
    const styles: Record<UserStatus, string> = {
      approved: 'bg-green-100 text-green-700',
      pending: 'bg-amber-100 text-amber-700',
      rejected: 'bg-red-100 text-red-700',
    }
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[status]}`}>{status}</span>
  }

  const fmtDate = (d: string) => {
    if (!d) return '—'
    return new Date(d).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-[var(--color-text-primary)]">User Access Management</h1>
        <button
          onClick={loadUsers}
          className="px-4 py-2 text-sm font-medium rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]"
        >
          🔄 Refresh
        </button>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {loading ? (
        <p className="text-[var(--color-text-muted)]">Loading users…</p>
      ) : users.length === 0 ? (
        <p className="text-[var(--color-text-muted)]">No users yet.</p>
      ) : (
        <div className="border border-[var(--color-border)] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-bg-tertiary)]">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">User</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Status</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Role</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">First Seen</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Last Seen</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-[var(--color-text-muted)] uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-t border-[var(--color-border)]">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      {u.avatar_url && <img src={u.avatar_url} alt="" className="w-6 h-6 rounded-full" />}
                      <div>
                        <div className="font-medium text-[var(--color-text-primary)]">{u.name || '—'}</div>
                        <div className="text-xs text-[var(--color-text-muted)] font-mono">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">{statusBadge(u.status)}</td>
                  <td className="px-4 py-2.5 text-[var(--color-text-secondary)]">{u.role}</td>
                  <td className="px-4 py-2.5 text-[var(--color-text-secondary)] text-xs">{fmtDate(u.first_seen)}</td>
                  <td className="px-4 py-2.5 text-[var(--color-text-secondary)] text-xs">{fmtDate(u.last_seen)}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    {u.status !== 'approved' && (
                      <button
                        onClick={() => setStatus(u.id, 'approved')}
                        className="px-2 py-1 text-xs rounded bg-green-100 text-green-700 hover:bg-green-200 mr-1"
                      >Approve</button>
                    )}
                    {u.status !== 'rejected' && (
                      <button
                        onClick={() => setStatus(u.id, 'rejected')}
                        className="px-2 py-1 text-xs rounded bg-red-100 text-red-700 hover:bg-red-200 mr-1"
                      >Reject</button>
                    )}
                    {/* Guard: don't allow removing yourself */}
                    {u.email !== appUser?.email && (
                      <button
                        onClick={() => removeUser(u.id, u.email)}
                        className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
                      >Remove</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
